/**
 * "The four things ServiceNow punishes you for" — the judgement section.
 *
 * The differentiator against a bare ServiceNow MCP server is not the size of
 * the tool catalogue, it is the practitioner knowledge shipped alongside it.
 * So this section names four traps in the visitor's own vocabulary, points
 * each row at the real Apache-2.0 SKILL.md that encodes the answer, and ends
 * on one verbatim excerpt — showing the judgement instead of counting it.
 *
 * Copy: every user-visible sentence comes from the `pitfalls` namespace in
 * locales/{en,nl}/marketing.json (see the key contract). The only strings
 * authored here are repository paths and one verbatim quote from
 * packages/skills/blast-radius/SKILL.md — source material, not copy. A quote
 * is a quote in both locales; translating it would falsify it.
 *
 * Each item names its own guide via an optional `skill` field (the directory
 * name under packages/skills/), so a copy reorder can no longer make a row
 * cite the wrong SKILL.md. SOURCES stays only as a positional fallback for an
 * item that does not carry one; an item with neither renders without a
 * citation rather than with a wrong one.
 */
import { useTranslation } from 'react-i18next';
import { Icon } from '../serac';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { CATALOG_SOURCE_URLS } from '../../generated/catalog-counts';

const SKILLS_ROOT = 'packages/skills/';
const REPO_BLOB = 'https://github.com/serac-labs/serac/blob/main/';

/** Fallback skill directories on serac-labs/serac@main, in `pitfalls.items` order. */
const SOURCES = [
  'es5-compliance',
  'update-set-workflow',
  'flow-designer',
  'blast-radius',
] as const;

/**
 * Verbatim from packages/skills/blast-radius/SKILL.md on main (the "Known
 * Limitations" section): the heading, its lead-in and the first four of eight
 * bullets, then the paragraph the section closes on. The four bullets are
 * consecutive in the source and the one cut is marked, so nothing here is
 * presented as adjacent that is not, and no quoted line is edited or
 * truncated.
 */
const EXCERPT_DIR = 'blast-radius';
const EXCERPT_LINES: string[] = [
  '## Known Limitations',
  '',
  'These gaps are inherent to static regex-based analysis over the REST Table API:',
  '',
  '- **Reference dotwalks**: `current.assignment_group.manager` is parsed as a reference to `assignment_group`, not to `sys_user_group.manager`. Dotwalked field changes are not flagged transitively.',
  '- **Dynamic field access**: `gr.setValue(someVar, value)` cannot be resolved because `someVar` is computed at runtime.',
  "- **Dynamic table access**: `new GlideRecord(tableVar)` — global artifacts won't be matched unless the table name appears literally in the script.",
  '- **Legacy workflows** (`wf_workflow`, `wf_activity`) are not scanned — migrating to Flow Designer is recommended anyway.',
  '[… four more limitations]',
  '',
  'If the tool reports "0 references", treat it as "no *static* references found in the artifact types we scan" — not as a proof of safety. For high-risk changes (renaming a widely-used field), combine with grep of the export XML, and consider running a sandboxed test after the change.',
];

interface PitfallItem {
  trap: string;
  answer: string;
  skill?: string;
}

/**
 * A citation: the real path of the guide, linked to the file on GitHub.
 * A path is not copy — it renders identically in every locale.
 */
function SourceLink({ dir, size = 11 }: { dir: string; size?: number }) {
  return (
    <a
      href={`${REPO_BLOB}${SKILLS_ROOT}${dir}/SKILL.md`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: 'var(--font-mono)',
        fontSize: size,
        lineHeight: 1.5,
        color: 'var(--fg-muted)',
        textDecoration: 'none',
        wordBreak: 'break-all',
        transition: 'color 150ms',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--ice-300)';
        e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--fg-muted)';
        e.currentTarget.style.textDecoration = 'none';
      }}
      onFocus={(e) => {
        e.currentTarget.style.color = 'var(--ice-300)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.color = 'var(--fg-muted)';
      }}
    >
      <span>
        <span style={{ opacity: 0.6 }}>{SKILLS_ROOT}</span>
        {dir}/SKILL.md
      </span>
      <Icon.external s={size} aria-hidden="true" />
    </a>
  );
}

export default function SectionPitfalls() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();

  // `returnObjects` on a key the copy agent has not filled yet returns the key
  // string, so guard the shape rather than mapping over a string.
  const raw = t('pitfalls.items', { returnObjects: true }) as unknown;
  const items: PitfallItem[] = Array.isArray(raw)
    ? (raw as unknown[]).filter(
        (x): x is PitfallItem =>
          !!x &&
          typeof x === 'object' &&
          typeof (x as PitfallItem).trap === 'string' &&
          typeof (x as PitfallItem).answer === 'string',
      )
    : [];

  // The four rows are the section. Without them the header, the tier note and
  // the excerpt are a caption with nothing to caption, so the band drops out
  // whole — the same way SectionAlternatives degrades.
  if (items.length === 0) return null;

  const skillsLink = t('pitfalls.skillsLink', { defaultValue: '' });

  return (
    <section
      id="pitfalls"
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header — mirrors the two-column header used elsewhere on the page. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 20 : 56,
            alignItems: 'start',
            marginBottom: isMobile ? 32 : 48,
          }}
        >
          <div>
            <div className="t-eyebrow" style={{ marginBottom: 16 }}>
              {t('pitfalls.eyebrow')}
            </div>
            <h2
              className="t-display"
              style={{
                fontSize: 'clamp(28px, 5vw, 44px)',
                margin: 0,
                maxWidth: 520,
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
            >
              {t('pitfalls.headline')}
            </h2>
          </div>
          <p
            style={{
              fontSize: isMobile ? 15 : 17,
              lineHeight: 1.6,
              color: 'var(--fg-muted)',
              margin: 0,
              maxWidth: 560,
            }}
          >
            {t('pitfalls.body')}
          </p>
        </div>

        {/* The four traps. Hairline-separated rows, spec-sheet rhythm. */}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 12,
            background: 'var(--surface)',
            overflow: 'hidden',
          }}
        >
          {items.map((item, i) => {
            const dir: string | undefined =
              typeof item.skill === 'string' && item.skill.trim() ? item.skill : SOURCES[i];
            return (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '28px minmax(0, 300px) minmax(0, 1fr)',
                  gap: isMobile ? 8 : 24,
                  alignItems: 'start',
                  padding: isMobile ? '20px 18px' : '24px 28px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    color: 'var(--fg-dim)',
                    paddingTop: isMobile ? 0 : 3,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: isMobile ? 16 : 17,
                      fontWeight: 500,
                      color: 'var(--fg)',
                      letterSpacing: '-0.005em',
                      lineHeight: 1.35,
                    }}
                  >
                    {item.trap}
                  </div>
                  {dir && (
                    <div style={{ marginTop: 8 }}>
                      <SourceLink dir={dir} />
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? 14 : 15,
                    lineHeight: 1.6,
                    color: 'var(--fg-muted)',
                    marginTop: isMobile ? 10 : 0,
                  }}
                >
                  {item.answer}
                </div>
              </div>
            );
          })}
        </div>

        {/* Anything in here that is not free gets labelled in the same breath. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            marginTop: 16,
            padding: isMobile ? '14px 16px' : '14px 20px',
            border: '1px dashed var(--border-strong)',
            borderRadius: 10,
          }}
        >
          <span style={{ color: 'var(--fg-dim)', flexShrink: 0, marginTop: 2 }}>
            <Icon.card s={14} aria-hidden="true" />
          </span>
          <span style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--fg-muted)' }}>
            {t('pitfalls.tierNote')}
          </span>
        </div>

        {/* One real excerpt beats a count of guides. Verbatim, elision marked. */}
        <figure
          style={{
            margin: isMobile ? '28px 0 0' : '32px 0 0',
            border: '1px solid var(--border)',
            borderRadius: 12,
            background: 'var(--bg)',
            overflow: 'hidden',
          }}
        >
          <figcaption
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              padding: isMobile ? '10px 16px' : '10px 20px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface)',
            }}
          >
            <SourceLink dir={EXCERPT_DIR} size={12} />
          </figcaption>
          <div
            style={{
              padding: isMobile ? 16 : 20,
              fontFamily: 'var(--font-mono)',
              fontSize: isMobile ? 11.5 : 12.5,
              lineHeight: 1.75,
              overflowX: 'auto',
            }}
          >
            {EXCERPT_LINES.map((line, i) => {
              const isHeading = line.startsWith('##');
              const isElision = line.startsWith('[…');
              return (
                <div
                  key={i}
                  style={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    minHeight: line === '' ? '0.9em' : undefined,
                    color: isHeading
                      ? 'var(--fg)'
                      : isElision
                        ? 'var(--fg-dim)'
                        : 'var(--fg-muted)',
                  }}
                >
                  {line}
                </div>
              );
            })}
          </div>
        </figure>

        {/* The count appears exactly once on the page, and only as a way in:
            one guide is shown above, the rest are one click away. */}
        {skillsLink.trim() !== '' && (
          <div style={{ marginTop: 16 }}>
            <a
              href={CATALOG_SOURCE_URLS.skills}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                lineHeight: 1.5,
                color: 'var(--ice-400)',
                textDecoration: 'none',
              }}
            >
              {skillsLink}
              <Icon.arrow s={14} aria-hidden="true" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
